<template>
  <div ref="wrapperRef" class="phone-number-field-wrapper">
    <div class="phone-number-field"
         :class="{ 'phone-number-field--error': !!props.error }">
      <div ref="phoneCodeTriggerRef"
           class="phone-number-field-trigger phone-code-trigger-wrap">
        <PhoneCodeButton :current-country-iso="currentCountryIso"
                         class="phone-number-field-button"
                         @click="openCodesPicker" />
      </div>
      <span class="phone-number-field-plus">+</span>
      <input ref="inputRef"
             :value="displayValue"
             type="tel"
             inputmode="numeric"
             :placeholder="props.placeholder"
             class="phone-number-field-input"
             v-bind="restAttrs"
             @input="onInput"
             @change="onChange"
             @blur="handleBlur"
             @keydown="handleKeydown" />
    </div>

    <UIError :message="props.error" />

    <!-- Мобильные: диалог на весь экран -->
    <PhoneCodeSelectorDialog v-if="!isDesktop"
                             v-model="dialog"
                             :search-query="searchQuery"
                             @update:search-query="searchQuery = $event"
                             @select="onSelectCountry" />

    <!-- Десктоп: popover над/под инпутом -->
    <PhoneCodeSelectorPopover v-if="isDesktop"
                              v-model="popoverOpen"
                              :search-query="searchQuery"
                              :dropdown-style="popoverDropdownStyle"
                              @update:search-query="searchQuery = $event"
                              @select="onSelectCountry" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, toRef, useAttrs, computed, nextTick } from 'vue'
import {
  usePhoneCountrySelection,
  usePhoneNumberSync,
  usePhoneCodeDialog,
} from '@/composables/phone'
import { PhoneCodes, type PhoneCode } from '@/constants/phone-codes'
import { usePhoneInputValidation } from '@/composables/phone'
import { useDropdown } from '@/composables/ui'
import PhoneCodeButton from './PhoneCodeButton.vue'
import PhoneCodeSelectorDialog from './PhoneCodeSelectorDialog.vue'
import PhoneCodeSelectorPopover from './PhoneCodeSelectorPopover.vue'
import UIError from '@/components/UI/UIError.vue'

const DESKTOP_BREAKPOINT_PX = 768

interface Props {
  /** Значення: тільки цифри (наприклад 380634050363); країну визначаємо по коду */
  modelValue?: string
  placeholder?: string
  error?: string
  /** Дефолтний телефонний код країни (напр. '373' для MD, '380' для UA) */
  defaultPhoneCode?: string
}

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'complete'): void
  (e: 'countryChanged'): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  defaultPhoneCode: '',
})

const emit = defineEmits<Emits>()

const attrs = useAttrs() as Record<string, unknown>
const { value: _value, onInput: _onInput, onBlur: _onBlur, ...restAttrs } = attrs

const phoneNumber = ref(props.modelValue ? `+${String(props.modelValue).replace(/\D/g, '')}` : '')

const {
  currentCountryIso,
  setCode,
  initializeCode,
  getCountryByIsoCode,
  getCountryByCode,
} = usePhoneCountrySelection(props.defaultPhoneCode)

const wrapperRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

const { dialog, searchQuery, openCodesDialog, selectCountry } = usePhoneCodeDialog(
  phoneNumber,
  setCode,
  () => emit('countryChanged'),
  () => getCountryByIsoCode(currentCountryIso.value) ?? null
)

const isDesktop = ref(false)
const phoneCodeTriggerRef = ref<HTMLElement | null>(null)
const popoverOpen = ref(false)

const { dropdownStyle: popoverDropdownStyle } = useDropdown(popoverOpen, phoneCodeTriggerRef, {
  triggerSelector: '.phone-code-trigger-wrap',
  dropdownSelector: '.phone-code-popover',
  offset: 8,
  itemHeight: 48,
  minHeight: 120,
  maxHeight: 360,
})

function openCodesPicker() {
  searchQuery.value = ''
  if (isDesktop.value) {
    popoverOpen.value = true
  } else {
    openCodesDialog()
  }
}

async function onSelectCountry(country: PhoneCode) {
  selectCountry(country)
  if (isDesktop.value) {
    popoverOpen.value = false
  }
  await nextTick()
  inputRef.value?.focus()
}

function updateIsDesktop() {
  if (typeof window === 'undefined') return
  isDesktop.value = window.innerWidth >= DESKTOP_BREAKPOINT_PX
}

const { handleKeydown } = usePhoneInputValidation()

const { syncFromProps, syncToModel, syncCountryFromInput } = usePhoneNumberSync(
  toRef(props, 'modelValue'),
  phoneNumber,
  currentCountryIso,
  setCode,
  (value) => emit('update:modelValue', value),
  () => emit('complete'),
  () => emit('countryChanged')
)

syncFromProps()
syncToModel()

/** У полі тільки цифри (код + номер), без + */
const displayValue = computed(() => phoneNumber.value.replace(/\D/g, ''))

function onInput(event: Event) {
  const target = (event.target as HTMLInputElement)
  const raw = target.value
  const digitsOnly = raw.replace(/\D/g, '')
  phoneNumber.value = digitsOnly ? `+${digitsOnly}` : ''
  if (raw !== digitsOnly) {
    target.value = digitsOnly
  }
}

/** Sync digits on blur/Enter — no country auto-detection, code stays as given */
function onChange() {
  const el = inputRef.value
  if (!el) return
  const digitsOnly = el.value.replace(/\D/g, '')
  const newVal = digitsOnly ? `+${digitsOnly}` : ''
  if (newVal !== phoneNumber.value) {
    phoneNumber.value = newVal
  }
}

/** Blur: валидируем только когда фокус ушёл за пределы поля (инпут + кнопка + поповер/диалог) */
function handleBlur(event: FocusEvent) {
  const next = event.relatedTarget as HTMLElement | null
  const wrapper = wrapperRef.value
  if (next && wrapper && wrapper.contains(next)) return
  if (next && typeof document !== 'undefined') {
    const inPopover = document.querySelector('.phone-code-popover')?.contains(next)
    const inDialog = document.querySelector('.phone-code-dialog-overlay')?.contains(next)
    if (inPopover || inDialog) return
  }
  onChange()
  if (typeof _onBlur === 'function') {
    (_onBlur as (e: FocusEvent) => void)(event)
  }
}

function getFullPrefix(country: PhoneCode): string {
  return country.phoneCode
}

watch(phoneNumber, (newValue) => {
  if (newValue?.trim()) return
  const country = currentCountryIso.value
    ? getCountryByIsoCode(currentCountryIso.value) ?? getCountryByCode(props.defaultPhoneCode)
    : getCountryByCode(props.defaultPhoneCode)
  if (country) {
    phoneNumber.value = `+${getFullPrefix(country)}`
  }
})

watch(isDesktop, (desktop) => {
  if (!desktop) popoverOpen.value = false
})

// Коли defaultPhoneCode змінюється (акаунт завантажився після mount) — оновлюємо
// тільки якщо користувач ще не ввів номер

onMounted(() => {
  updateIsDesktop()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateIsDesktop)
  }
  initializeCode(props.defaultPhoneCode)
  const digits = (phoneNumber.value || '').replace(/\D/g, '')
  const isOnlyCode = digits.length > 0 && PhoneCodes.some(c => c.phoneCode === digits)
  if (!digits || isOnlyCode) {
    const country = getCountryByCode(props.defaultPhoneCode)
    if (country) {
      phoneNumber.value = `+${getFullPrefix(country)}`
    }
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateIsDesktop)
  }
})

defineOptions({ inheritAttrs: false })
</script>

<style scoped>
.phone-number-field-wrapper {
  width: 100%;
}

.phone-number-field {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 56px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 0 8px 0 14px;
  background: #fff;
  transition: border-color 0.2s;
}

.phone-number-field:focus-within {
  outline: none;
  border-color: #09AFEA;
}

.phone-number-field--error {
  border: 2px solid var(--color-error, #ff3b66);
}

.phone-number-field--error:focus-within {
  outline: none;
  border-color: var(--color-error, #ff3b66);
}

.phone-number-field-trigger {
  display: flex;
  align-items: center;
  margin-right: 4px;
}

.phone-number-field-button {
  flex-shrink: 0;
}

.phone-number-field-plus {
  flex-shrink: 0;
  font-family: var(--font-inter);
  font-size: 16px;
  color: var(--color-dark-blue, #252840);
  margin-right: 2px;
}

.phone-number-field-input {
  flex: 1;
  min-width: 0;
  padding: 8px 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-inter);
  font-size: 16px;
  color: var(--color-dark-blue, #252840);
}

.phone-number-field-input::placeholder {
  color: var(--color-grey, #868D98);
}
@media (max-width: 480px) {
  .phone-number-field {
  min-height: 45px;
}
}
</style>
